/* eslint-disable @typescript-eslint/camelcase */

import { stringify } from 'qs';
import * as AdvancedReportsModes from './AdvancedReportsModes';
import { preprocessReceivedData, preprocessReceivedReport } from './AdvancedReportsHelpers';
import fetchWithDefaults from '../CommonUtils/fetchWithDefaults';
import { urlWithParams } from '../CommonUtils/urlWithParams';
import { MESSAGES } from '../CommonConstants';
import { HttpError } from '../errors/HttpError';
import { httpBodyOptions } from '../CommonUtils/httpBodyOptions';

export const INITIALIZE_SECTION = 'ADVANCED_REPORTS.INITIALIZE_SECTION';
export const SET_RECOMMENDED = 'ADVANCED_REPORTS.SET_RECOMMENDED';
export const SET_ERRORS = 'ADVANCED_REPORTS.SET_ERRORS';
export const RECEIVE_DATA = 'ADVANCED_REPORTS.RECEIVE_DATA';
export const SET_SELECTED_REPORT = 'ADVANCED_REPORTS.SET_SELECTED_REPORT';
export const RECEIVE_REPORT = 'ADVANCED_REPORTS.RECEIVE_REPORT';
export const SET_POPUP_OPEN = 'ADVANCED_REPORTS.SET_POPUP_OPEN';
export const SET_SAVING = 'ADVANCED_REPORTS.SET_SAVING';
export const SET_ERROR_TYPE = 'ADVANCED_REPORTS.SET_ERROR_TYPE';

export const initializeSection = (clientId, isAdviserArea) => ({
  type: INITIALIZE_SECTION,
  clientId,
  isAdviserArea,
});

export const setRecommended = (recommended, clientId) => ({
  type: SET_RECOMMENDED,
  recommended,
  clientId,
});

export const setSaving = (saving, clientId) => ({
  type: SET_SAVING,
  saving,
  clientId,
});

export const setPopupOpen = (isPopupOpen, clientId) => ({
  type: SET_POPUP_OPEN,
  isPopupOpen,
  clientId,
});

export const setSelectedReport = (selectedReport, clientId) => ({
  type: SET_SELECTED_REPORT,
  selectedReport,
  clientId,
});

export const setErrors = (errors, clientId) => ({
  type: SET_ERRORS,
  errors,
  clientId,
});

export const setErrorType = (errorType, clientId) => ({
  type: SET_ERROR_TYPE,
  errorType,
  clientId,
});

export const receiveData = (data, clientId) => ({
  type: RECEIVE_DATA,
  data,
  clientId,
});

export const receiveReport = (report, clientId) => ({
  type: RECEIVE_REPORT,
  report,
  clientId,
});

export const requestReport = (
  columns,
  mode,
  startYear,
  clientId,
  fpId,
  printDone,
  expandCurrentYear,
) => async (dispatch, getState) => {
  const body = {
    clientId,
    columns,
    mode,
    startYear,
    fpId,
    expandCurrentYear,
  };

  try {
    const response = await fetchWithDefaults('/Consumer/AdvancedReport/GenerateReport', {
      method: 'POST',
      ...httpBodyOptions(body),
      showSpinner: true,
    });

    if (response.ok) {
      const { payload, success, errors } = await response.json();

      if (success) {
        let data = null;

        if (getState().AdvancedReports.hasOwnProperty(clientId)) {
          const subdivision = getState().AdvancedReports[clientId];
          data = subdivision.data;
        }

        dispatch(receiveReport(preprocessReceivedReport(payload.viewData, data), clientId));

        if (printDone && typeof printDone === 'function') {
          printDone();
        }

        return;
      }

      dispatch(setErrors(errors, clientId));
      return;
    }

    throw new HttpError(response);
  } catch (err) {
    console.error(err); // eslint-disable-line no-console
    dispatch(setErrors([MESSAGES.GENERIC_ERROR], clientId));
  }
};

export const changeSelectedReportName = (name, clientId) => (dispatch, getState) => {
  if (getState().AdvancedReports.hasOwnProperty(clientId)) {
    const subdivision = getState().AdvancedReports[clientId];
    dispatch(
      setSelectedReport(
        {
          ...subdivision.selectedReport,
          name,
        },
        clientId,
      ),
    );
  }
};

export const changeSelectedReportType = (type, clientId) => (dispatch, getState) => {
  if (getState().AdvancedReports.hasOwnProperty(clientId)) {
    const subdivision = getState().AdvancedReports[clientId];
    dispatch(
      setSelectedReport(
        {
          ...subdivision.selectedReport,
          type,
        },
        clientId,
      ),
    );
  }
};

export const changeReportFirmLevel = (isFirmLevelReport, clientId) => (dispatch, getState) => {
  if (getState().AdvancedReports.hasOwnProperty(clientId)) {
    const subdivision = getState().AdvancedReports[clientId];
    dispatch(
      setSelectedReport(
        {
          ...subdivision.selectedReport,
          isFirmLevelReport,
        },
        clientId,
      ),
    );
  }
};

export const changeSelectedReportStartYear = (startYear, sortedColumns, clientId, fpId) => (
  dispatch,
  getState,
) => {
  if (getState().AdvancedReports.hasOwnProperty(clientId)) {
    const subdivision = getState().AdvancedReports[clientId];
    const current_report = { ...subdivision.selectedReport, startYear };

    current_report.columns = sortedColumns;

    dispatch(setSelectedReport(current_report, clientId));
    dispatch(
      requestReport(
        current_report.columns,
        current_report.mode,
        current_report.startYear,
        clientId,
        fpId,
      ),
    );
  }
};

export const fetchSelectedReport = (clientId, fpId, printDone, expandCurrentYear) => (
  dispatch,
  getState,
) => {
  if (getState().AdvancedReports.hasOwnProperty(clientId)) {
    const subdivision = getState().AdvancedReports[clientId];
    let mode = AdvancedReportsModes.CURRENT;
    const { startYear } = subdivision.selectedReport;

    if (subdivision.recommended) {
      mode = AdvancedReportsModes.RECOMMENDED;
    }
    const fetchedReport = {
      ...subdivision.selectedReport,
    };

    if (
      fetchedReport.name === '' &&
      fetchedReport.type === '' &&
      fetchedReport.columns.length === 0
    ) {
      fetchedReport.columns = subdivision.data.DefaultReports['Investment Assets'].Columns;
    }

    if (subdivision.selectedReport.columns.length !== 0) {
      dispatch(
        requestReport(
          fetchedReport.columns,
          mode,
          startYear,
          clientId,
          fpId,
          printDone,
          expandCurrentYear,
        ),
      );
    } else {
      dispatch(receiveReport(preprocessReceivedReport(null, subdivision.data), clientId));
    }
  }
};

export const saveReport = (
  name,
  columns,
  startYear,
  isFirmLevelReport,
  clientId,
  fpId,
  callback,
) => dispatch => {
  dispatch(setSaving(true, clientId));

  fetchWithDefaults('/Consumer/AdvancedReport/SaveReport', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: stringify({ clientId, fpId, name, columns, startYear, isFirmLevelReport }),
  })
    .then(response => response.json())
    .then(response => {
      if (response.success) {
        if (callback && typeof callback === 'function') {
          callback();
        }
      } else {
        dispatch(setErrors(response.errors, clientId));
      }
    })
    .catch(() => {
      dispatch(setErrors([ADV.settings.MsgLib.generic_error], clientId));
    })
    .catch()
    .then(() => dispatch(setSaving(false, clientId)));
};

export const deleteReport = (
  name,
  clientId,
  fpId,
  isFirmLevelReport,
  callback,
) => async dispatch => {
  try {
    const body = {
      clientId,
      name,
      fpId,
      isFirmLevelReport,
    };

    const response = await fetchWithDefaults('/Consumer/AdvancedReport/DeleteReport', {
      method: 'POST',
      ...httpBodyOptions(body),
      showSpinner: true,
    });

    if (response.ok) {
      const { success, errors } = await response.json();

      if (success) {
        if (callback && typeof callback === 'function') {
          callback();
        }
        return;
      }

      dispatch(setErrors(errors, clientId));
      return;
    }

    throw new HttpError(response);
  } catch (err) {
    console.error(err.toString()); // eslint-disable-line no-console
    dispatch(setErrors([MESSAGES.GENERIC_ERROR], clientId));
  }
};

export const requestData = (clientId, fpId, onSuccessCallback) => async dispatch => {
  try {
    const response = await fetchWithDefaults(
      urlWithParams('/Consumer/AdvancedReport', {
        clientId,
        fpId,
      }),
      {
        showSpinner: true,
      },
    );

    if (response.ok) {
      const { payload, success, errors, errorType } = await response.json();

      if (success) {
        dispatch(receiveData(preprocessReceivedData(payload.viewData), clientId));

        if (onSuccessCallback && typeof onSuccessCallback === 'function') {
          onSuccessCallback.call(undefined);
        }
        return;
      }

      dispatch(setErrorType(errorType, clientId));
      dispatch(setErrors(errors, clientId));
      return;
    }

    throw new HttpError(response);
  } catch (err) {
    console.error(err); // eslint-disable-line no-console
    dispatch(setErrors([MESSAGES.GENERIC_ERROR], clientId));
  }
};

export const setColumns = (columns, clientId, fpId) => (dispatch, getState) => {
  const subdivision = getState().AdvancedReports[clientId];
  const current_start_year = subdivision ? subdivision.selectedReport.startYear : 0;

  dispatch(
    setSelectedReport(
      {
        type: '',
        name: '',
        columns,
        startYear: current_start_year,
      },
      clientId,
    ),
  );
  setTimeout(() => dispatch(fetchSelectedReport(clientId, fpId)), 0);
};

export const setSelectedReportByName = (reportName, clientId, fpId, printDone) => (
  dispatch,
  getState,
) => {
  if (!getState().AdvancedReports.hasOwnProperty(clientId)) {
    return;
  }
  const subdivision = getState().AdvancedReports[clientId];

  const Reports = {
    DefaultReports: subdivision.data.DefaultReports,
    SavedReports: subdivision.data.SavedReports,
    FirmLevelReports: subdivision.data.FirmLevelReports,
  };
  let foundReport;

  for (let reportType in Reports) {
    if (Reports.hasOwnProperty(reportType)) {
      for (let report in Reports[reportType]) {
        if (Reports[reportType] && Reports[reportType].hasOwnProperty(report)) {
          if (report === reportName) {
            foundReport = {
              type: reportType,
              name: reportName,
              columns: Reports[reportType][report].Columns,
              startYear: Reports[reportType][report].StartYear,
              isDeletable: Reports[reportType][report].CanDelete,
              isFirmLevelReport: Reports[reportType][report].IsFirmLevelReport,
            };
          }
        }
      }
    }
  }

  if (foundReport) {
    dispatch(setSelectedReport(foundReport, clientId));
    setTimeout(() => dispatch(fetchSelectedReport(clientId, fpId, printDone)), 0);
  }
};
